/**
 * SkipLink Component
 *
 * Provides a "Skip to main content" link for keyboard users and screen readers.
 * This link is visually hidden until it receives focus, then appears at the
 * top-left of the viewport.
 *
 * WCAG 2.1 AA Requirement: Skip links allow keyboard users to bypass
 * repetitive navigation and jump directly to main content.
 *
 * @example
 * // In App.tsx, add as the first child of the main container:
 * <SkipLink />
 * <nav>...</nav>
 * <main id="main-content">...</main>
 */

interface SkipLinkProps {
  /** The target element ID to skip to (default: "main-content") */
  targetId?: string;
  /** Custom label text (default: "Skip to main content") */
  label?: string;
}

export function SkipLink({
  targetId = "main-content",
  label = "Skip to main content",
}: SkipLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      // Set tabindex to make the element focusable if it isn't already
      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus();
      // Scroll to the element
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <a href={`#${targetId}`} className="skip-link" onClick={handleClick}>
      {label}
    </a>
  );
}

export default SkipLink;
